import { COMPACT_GRID_WIDTH } from '../app/constants';
import { MINIMIZED_CALL_DOCK_RESERVE_CSS_VAR } from '../../toolkit/styles/callMinimize.constants';

/** Viewport dimensions used for drag boundary calculations. */
export interface ViewportSize {
  width: number;
  height: number;
}

/** Axis-aligned rectangle for clamping a draggable card's top-left corner. */
export interface DragBoundaryRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Options for {@link getJustInTimeDragBounds}. */
export interface GetJustInTimeDragBoundsOptions {
  isMobile: boolean;
  dockReservePx?: number;
}

/** Vertical offset between default-stacked JIT cards (px). */
export const JUST_IN_TIME_CARD_STACK_STAGGER_PX = 120;

/** Default z-index for JIT call cards (below SpeedDial overlay at 999/1000). */
export const JUST_IN_TIME_CALL_Z_INDEX = 900;

/** z-index while actively dragging a JIT card. */
export const JUST_IN_TIME_CALL_DRAG_Z_INDEX = 901;

const DESKTOP_RIGHT_PADDING_PX = 16;
const MOBILE_RIGHT_PADDING_PX = 8;
const MOBILE_LEFT_PADDING_PX = 8;
const DESKTOP_TOP_OFFSET_PX = 100;
const MOBILE_TOP_OFFSET_PX = 80;
const BOTTOM_PADDING_PX = 16;

/**
 * Parses the compact side-nav width from layout constants.
 *
 * @returns Side navigation width in pixels.
 */
export function parseCompactGridWidthPx(): number {
  return parseInt(COMPACT_GRID_WIDTH, 10);
}

/**
 * Reads minimized-call dock reserve height from the document root CSS variable.
 *
 * @param root - Element to read computed styles from.
 * @returns Dock reserve height in pixels, or 0 when unset.
 */
export function readMinimizedCallDockReservePx(
  root: HTMLElement = document.documentElement
): number {
  const raw = getComputedStyle(root)
    .getPropertyValue(MINIMIZED_CALL_DOCK_RESERVE_CSS_VAR)
    .trim();
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
 * Computes the draggable region for JIT call cards within the main content area.
 *
 * @param viewport - Current viewport width and height.
 * @param options - Mobile flag and optional dock reserve override.
 * @returns Boundary rectangle for card top-left clamping.
 */
export function getJustInTimeDragBounds(
  viewport: ViewportSize,
  options: GetJustInTimeDragBoundsOptions
): DragBoundaryRect {
  const dockReservePx = options.dockReservePx ?? 0;
  const left = options.isMobile
    ? MOBILE_LEFT_PADDING_PX
    : parseCompactGridWidthPx();

  return {
    left,
    top: options.isMobile ? MOBILE_TOP_OFFSET_PX : DESKTOP_TOP_OFFSET_PX,
    right: viewport.width - (options.isMobile ? MOBILE_RIGHT_PADDING_PX : DESKTOP_RIGHT_PADDING_PX),
    bottom: viewport.height - BOTTOM_PADDING_PX - dockReservePx,
  };
}

/**
 * Clamps a card's top-left position so the full card stays inside the boundary rect.
 *
 * @param x - Proposed left position in pixels.
 * @param y - Proposed top position in pixels.
 * @param cardWidth - Measured card width in pixels.
 * @param cardHeight - Measured card height in pixels.
 * @param bounds - Allowed drag region.
 * @returns Clamped top-left position.
 */
export function clampPointToBounds(
  x: number,
  y: number,
  cardWidth: number,
  cardHeight: number,
  bounds: DragBoundaryRect
): { x: number; y: number } {
  const maxLeft = Math.max(bounds.left, bounds.right - cardWidth);
  const maxTop = Math.max(bounds.top, bounds.bottom - cardHeight);

  return {
    x: Math.min(Math.max(x, bounds.left), maxLeft),
    y: Math.min(Math.max(y, bounds.top), maxTop),
  };
}

/**
 * Default top-right position for a newly visible JIT card with vertical stagger.
 *
 * @param params - Stack index, card size, and boundary rect.
 * @returns Clamped default position for the card top-left corner.
 */
export function getDefaultJustInTimeCardPosition(params: {
  stackIndex: number;
  cardWidth: number;
  cardHeight: number;
  bounds: DragBoundaryRect;
}): { x: number; y: number } {
  const { stackIndex, cardWidth, cardHeight, bounds } = params;
  const x = bounds.right - cardWidth;
  const y = bounds.top + stackIndex * JUST_IN_TIME_CARD_STACK_STAGGER_PX;

  return clampPointToBounds(x, y, cardWidth, cardHeight, bounds);
}
